// src/pages/SportsPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SportsSidebar from '../components/sports/SportsSidebar';
import MatchCard from '../components/sports/MatchCard';
import { useBetSlip } from '../context/BetSlipContext';
import { FiFilter, FiGrid, FiList, FiMenu } from 'react-icons/fi';

const SportsPage = () => {
  const [selectedSport, setSelectedSport] = useState('football');
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [oddsFormat, setOddsFormat] = useState('decimal');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const navigate = useNavigate();
  const { betslipCount } = useBetSlip();

  // Demo sports data
  const sportsData = {
    football: {
      name: 'Football',
      matches: [
        {
          id: 1,
          homeTeam: 'Manchester United',
          awayTeam: 'Manchester City',
          league: 'Premier League',
          startTime: '2024-12-15T15:00:00',
          odds: {
            home: 3.20,
            draw: 3.50,
            away: 2.10
          }
        },
        {
          id: 2,
          homeTeam: 'Real Madrid',
          awayTeam: 'Barcelona',
          league: 'La Liga',
          startTime: '2024-12-16T20:00:00',
          odds: {
            home: 2.40,
            draw: 3.30,
            away: 2.80
          }
        },
        {
          id: 3,
          homeTeam: 'Bayern Munich',
          awayTeam: 'Borussia Dortmund',
          league: 'Bundesliga',
          startTime: '2024-12-14T17:30:00',
          odds: {
            home: 1.80,
            draw: 3.80,
            away: 4.20
          }
        },
        {
          id: 4,
          homeTeam: 'Paris Saint-Germain',
          awayTeam: 'Marseille',
          league: 'Ligue 1',
          startTime: '2024-12-17T21:00:00',
          odds: {
            home: 1.45,
            draw: 4.20,
            away: 6.50
          }
        }
      ]
    },
    basketball: {
      name: 'Basketball',
      matches: [
        {
          id: 5,
          homeTeam: 'LA Lakers',
          awayTeam: 'Golden State Warriors',
          league: 'NBA',
          startTime: '2024-12-15T23:00:00',
          odds: {
            home: 1.90,
            away: 1.90
          }
        },
        {
          id: 6,
          homeTeam: 'Boston Celtics',
          awayTeam: 'Miami Heat',
          league: 'NBA',
          startTime: '2024-12-16T19:30:00',
          odds: {
            home: 2.10,
            away: 1.75
          }
        }
      ]
    },
    tennis: {
      name: 'Tennis',
      matches: [
        {
          id: 7,
          player1: 'Novak Djokovic',
          player2: 'Rafael Nadal',
          tournament: 'ATP Finals',
          startTime: '2024-12-16T14:00:00',
          odds: {
            player1: 1.60,
            player2: 2.30
          }
        },
        {
          id: 8,
          player1: 'Carlos Alcaraz',
          player2: 'Jannik Sinner',
          tournament: 'ATP Finals',
          startTime: '2024-12-17T16:00:00',
          odds: {
            player1: 1.75,
            player2: 2.10
          }
        }
      ]
    },
    cricket: {
      name: 'Cricket',
      matches: [
        {
          id: 9,
          homeTeam: 'India',
          awayTeam: 'Australia',
          league: 'Test Series',
          startTime: '2024-12-18T09:30:00',
          odds: {
            home: 1.80,
            draw: 4.50,
            away: 3.20
          }
        }
      ]
    }
  };

  useEffect(() => {
    // Simulate API call
    setLoading(true);
    setTimeout(() => {
      setMatches(sportsData[selectedSport]?.matches || []);
      setLoading(false);
    }, 500);
  }, [selectedSport]);

  const addToBetslip = (match, selection, odds) => {
    const bet = {
      id: `${match.id}-${selection}`,
      match: `${match.homeTeam || match.player1} vs ${match.awayTeam || match.player2}`,
      selection,
      odds,
      sport: selectedSport
    };
    
    // Add to local storage or context
    const currentBets = JSON.parse(localStorage.getItem('betslip') || '[]');
    currentBets.push(bet);
    localStorage.setItem('betslip', JSON.stringify(currentBets));
    
    alert(`${selection} added to betslip!`);
  };

  // Mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsFilterOpen(false);
  };

  // Mobile filter toggle
  const toggleFilterMenu = () => {
    setIsFilterOpen(!isFilterOpen);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-deep-black pt-16"> {/* Add pt-16 for fixed header */}
      <Header />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Sports Betting</h1>
            
            {/* Mobile Menu Buttons */}
            <div className="flex sm:hidden items-center gap-2">
              <button
                onClick={toggleMobileMenu}
                className="p-2 bg-gray-800 rounded-lg"
              >
                <FiMenu className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={toggleFilterMenu}
                className="p-2 bg-gray-800 rounded-lg"
              >
                <FiFilter className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {/* View Mode Toggle - Hidden on mobile */}
            <div className="hidden sm:flex items-center space-x-2 bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-700' : ''}`}
              >
                <FiGrid className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-700' : ''}`}
              >
                <FiList className="w-4 h-4 text-white" />
              </button>
            </div>
            
            {/* Odds Format Selector */}
            <select 
              value={oddsFormat}
              onChange={(e) => setOddsFormat(e.target.value)}
              className="w-full sm:w-auto bg-gray-800 text-white px-3 sm:px-4 py-2 rounded-lg text-sm"
            >
              <option value="decimal">Decimal (2.0)</option>
              <option value="fractional">Fractional (1/1)</option>
              <option value="american">American (+100)</option>
            </select>
            
            {/* Betslip Button */}
            <Link 
              to="/betslip" 
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-2 sm:py-2 rounded-lg font-semibold text-center text-sm sm:text-base"
            >
              Betslip ({betslipCount})
            </Link>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Mobile Sidebar Overlay */}
          {isMobileMenuOpen && (
            <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleMobileMenu}>
              <div className="absolute left-0 top-0 h-full w-64 bg-gray-900 shadow-lg" onClick={(e) => e.stopPropagation()}>
                <div className="p-4">
                  <h3 className="text-lg font-bold text-white mb-4">Sports Categories</h3>
                  <SportsSidebar 
                    selectedSport={selectedSport}
                    onSportSelect={(sport) => {
                      setSelectedSport(sport);
                      setIsMobileMenuOpen(false);
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Mobile Filter Overlay */}
          {isFilterOpen && (
            <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleFilterMenu}>
              <div className="absolute right-0 top-0 h-full w-64 bg-gray-900 shadow-lg p-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-white mb-4">Filter Options</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-300 mb-2">Odds Format</label>
                    <select 
                      value={oddsFormat}
                      onChange={(e) => setOddsFormat(e.target.value)}
                      className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg"
                    >
                      <option value="decimal">Decimal (2.0)</option>
                      <option value="fractional">Fractional (1/1)</option>
                      <option value="american">American (+100)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">View Mode</label>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`flex-1 p-2 rounded ${viewMode === 'grid' ? 'bg-blue-600' : 'bg-gray-800'}`}
                      >
                        <FiGrid className="w-4 h-4 text-white mx-auto" />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`flex-1 p-2 rounded ${viewMode === 'list' ? 'bg-blue-600' : 'bg-gray-800'}`}
                      >
                        <FiList className="w-4 h-4 text-white mx-auto" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sidebar - Hidden on mobile, visible on desktop */}
          <div className="hidden lg:block w-full lg:w-64 flex-shrink-0">
            <SportsSidebar 
              selectedSport={selectedSport}
              onSportSelect={setSelectedSport}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Sport Header */}
            <div className="bg-gray-900 rounded-xl p-4 mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                    {sportsData[selectedSport]?.name || 'Sport'} Matches
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Today's matches and betting odds
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <span className="hidden sm:inline">Showing</span>
                  <span className="bg-gray-800 px-3 py-1 rounded-lg">
                    {matches.length} matches
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile View Mode Toggle */}
            <div className="lg:hidden flex items-center justify-between mb-4">
              <div className="text-sm text-gray-400">
                {matches.length} matches available
              </div>
              <div className="flex items-center space-x-2 bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-700' : ''}`}
                >
                  <FiGrid className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-700' : ''}`}
                >
                  <FiList className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Matches List */}
            {loading ? (
              <div className="text-center py-8 sm:py-12">
                <div className="inline-block animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-blue-500"></div>
                <p className="text-gray-400 mt-4 text-sm sm:text-base">Loading matches...</p>
              </div>
            ) : matches.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <div className="text-4xl sm:text-5xl mb-4">⚽</div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">No matches available</h3>
                <p className="text-gray-400 mb-6 text-sm sm:text-base">
                  No matches available for this sport at the moment.
                </p>
                <button
                  onClick={() => setSelectedSport('football')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
                >
                  Back to Football
                </button>
              </div>
            ) : (
              <div className={viewMode === 'grid' 
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-4" 
                : "space-y-3 sm:space-y-4"
              }>
                {matches.map(match => (
                  <MatchCard
                    key={match.id}
                    match={match}
                    sport={selectedSport}
                    oddsFormat={oddsFormat}
                    onAddToBetslip={addToBetslip}
                    viewMode={viewMode}
                  />
                ))}
              </div>
            )}

            {/* Live Events Banner */}
            <div className="mt-6 sm:mt-8 bg-gradient-to-r from-red-900/30 to-red-800/20 border border-red-700/30 rounded-xl p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <h3 className="text-lg sm:text-xl font-bold text-white">🔥 Live Betting</h3>
                  </div>
                  <p className="text-gray-300 text-sm sm:text-base">
                    Bet on ongoing matches with real-time odds
                  </p>
                </div>
                <Link 
                  to="/live" 
                  className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold text-center text-sm sm:text-base"
                >
                  Go to Live Betting
                </Link>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gray-900 rounded-lg p-3 sm:p-4 text-center">
                <div className="text-lg sm:text-2xl font-bold text-white mb-1">
                  {matches.length}
                </div>
                <div className="text-xs sm:text-sm text-gray-400">Total Matches</div>
              </div>
              <div className="bg-gray-900 rounded-lg p-3 sm:p-4 text-center">
                <div className="text-lg sm:text-2xl font-bold text-green-400 mb-1">
                  247
                </div>
                <div className="text-xs sm:text-sm text-gray-400">Live Odds</div>
              </div>
              <div className="bg-gray-900 rounded-lg p-3 sm:p-4 text-center">
                <div className="text-lg sm:text-2xl font-bold text-blue-400 mb-1">
                  42
                </div>
                <div className="text-xs sm:text-sm text-gray-400">Markets</div>
              </div>
              <div className="bg-gray-900 rounded-lg p-3 sm:p-4 text-center">
                <div className="text-lg sm:text-2xl font-bold text-yellow-400 mb-1">
                  98%
                </div>
                <div className="text-xs sm:text-sm text-gray-400">Uptime</div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Quick Navigation */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 p-3 z-30">
          <div className="flex justify-around">
            <button
              onClick={toggleMobileMenu}
              className="flex flex-col items-center text-gray-400 hover:text-white"
            >
              <FiMenu className="w-5 h-5 mb-1" />
              <span className="text-xs">Sports</span>
            </button>
            <Link
              to="/betslip"
              className="flex flex-col items-center text-gray-400 hover:text-white"
            >
              <div className="relative">
                <span className="text-lg">📋</span>
                {betslipCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {betslipCount}
                  </span>
                )}
              </div>
              <span className="text-xs">Betslip</span>
            </Link>
            <button
              onClick={toggleFilterMenu}
              className="flex flex-col items-center text-gray-400 hover:text-white"
            >
              <FiFilter className="w-5 h-5 mb-1" />
              <span className="text-xs">Filter</span>
            </button>
            <Link
              to="/live"
              className="flex flex-col items-center text-gray-400 hover:text-white"
            >
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse mb-1"></div>
              <span className="text-xs">Live</span>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SportsPage;